import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as NewChatIcon,
  Chat as ChatIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Toll as TokenIcon,
  ExpandMore as ExpandMoreIcon,
  ClearAll as ClearAllIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import AgentIcon from '@mui/icons-material/Psychology';

interface AgentQuestion {
  id: string;
  session_id: string | null;
  question: string;
  answer: string | null;
  items: string[];
  confidence: number | null;
  data_sources_used: string[];
  caveats: string | null;
  error: string | null;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  thinking_tokens: number;
  total_tokens: number;
  estimated_cost_usd: number;
  created_at: string;
}

interface AgentSession {
  session_id: string;
  title: string;
  message_count: number;
  created_at: string;
  last_active_at: string;
}

interface UsageSummary {
  total_questions: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_thinking_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
}

interface AICredits {
  credits_remaining: number;
  credits_monthly_allowance: number;
}

/** 6dp is enough to show a non-zero figure for a single cheap question (typically well under
 * a cent) without rendering as a misleading flat "$0.00" for every individual message. */
const fmtCostUsd = (v: number) => `$${v.toFixed(6)}`;

/**
 * Renders the agent's answer: the short prose summary, plus (when the model enumerated
 * anything) a real bullet list built from the structured `items` array. Earlier this parsed
 * "- "-prefixed lines out of the free-text `answer` itself, but Gemini didn't reliably emit
 * literal newlines inside a single string field (items kept running together on one line) —
 * `items` is a separate schema field instead, which structured output enforces as a real JSON
 * array regardless of that formatting fragility.
 */
const AnswerText: React.FC<{ text: string; items: string[] }> = ({ text, items }) => (
  <Box>
    <Typography sx={{ color: 'text.secondary' }}>{text}</Typography>
    {items.length > 0 && (
      <List sx={{ listStyleType: 'disc', pl: 3, py: 0, mt: 0.5 }}>
        {items.map((item, i) => (
          <ListItem key={i} sx={{ display: 'list-item', color: 'text.secondary', p: 0, mb: 0.5 }}>
            {item}
          </ListItem>
        ))}
      </List>
    )}
  </Box>
);

/**
 * "Ask AI" — POC portfolio Q&A agent (see backend/portfolio_agent.py). Chat-style: a left
 * sidebar of previous chats (grouped by AgentQuestion.session_id) plus the active chat's
 * messages on the right. No conversation memory in the agent itself — each question is still
 * answered independently against a fresh data snapshot; session_id only groups messages for
 * this UI, it isn't fed back into the LLM call.
 */
const AskAIPage: React.FC = () => {
  const { palette } = useTheme();
  const { token: authToken } = useAuth();
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AgentQuestion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [credits, setCredits] = useState<AICredits | null>(null);
  // Which messages in the active chat are shown fully expanded — only the most recent one is
  // expanded by default (see openSession/handleAsk, which reset this to just the newest id
  // whenever the chat changes or a new question is answered); everything else renders as a
  // collapsed summary row until clicked.
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggleOpen = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const token = () => authToken || localStorage.getItem('auth_token');
  const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' });

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/api/agent/sessions`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
        return data.sessions || [];
      }
    } finally {
      setSessionsLoading(false);
    }
    return [];
  };

  const fetchUsageSummary = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/api/agent/usage-summary`, { headers: headers() });
      if (res.ok) {
        setUsageSummary(await res.json());
      }
    } catch {
      // Non-critical — the sidebar just won't show a running total this load.
    }
  };

  const fetchCredits = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/api/agent/credits`, { headers: headers() });
      if (res.ok) {
        setCredits(await res.json());
      }
    } catch {
      // Non-critical — the sidebar just won't show a credit balance this load.
    }
  };

  const openSession = async (sessionId: string) => {
    setHistoryLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/agent/history?session_id=${sessionId}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        // Backend returns most-recent-first; a chat reads naturally oldest-first, newest at the bottom.
        const chronological = (data.questions || []).slice().reverse();
        setHistory(chronological);
        setActiveSessionId(sessionId);
        const latest = chronological[chronological.length - 1];
        setOpenIds(latest ? new Set([latest.id]) : new Set());
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const list = await fetchSessions();
      if (list.length > 0) {
        await openSession(list[0].session_id);
      } else {
        setHistoryLoading(false);
      }
    })();
    fetchUsageSummary();
    fetchCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewChat = () => {
    setActiveSessionId(null);
    setHistory([]);
    setOpenIds(new Set());
    setQuestion('');
    setError(null);
    fetchSessions(); // the chat just left behind should now appear in the sidebar
  };

  const handleAsk = async () => {
    const q = question.trim();
    if (!q || asking) return;
    setAsking(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/agent/ask`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ question: q, session_id: activeSessionId }),
      });
      const data = await res.json();
      if (!res.ok) {
        // A 402 out-of-credits response still carries the (now-zero-ish) balance — keep the
        // sidebar's figure in sync with it rather than leaving the stale pre-request balance shown.
        if (res.status === 402 && typeof data.credits_remaining === 'number') {
          setCredits({ credits_remaining: data.credits_remaining, credits_monthly_allowance: data.credits_monthly_allowance });
        }
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      setHistory(prev => [...prev, data.result]);
      setOpenIds(new Set([data.result.id])); // the just-answered question is the only one expanded now
      if (!activeSessionId) {
        setActiveSessionId(data.result.session_id);
        fetchSessions(); // this brand-new chat should now appear in the sidebar too
      }
      if (typeof data.credits_remaining === 'number') {
        setCredits({ credits_remaining: data.credits_remaining, credits_monthly_allowance: data.credits_monthly_allowance });
      }
      fetchUsageSummary(); // every question changes the running token/cost total
      setQuestion('');
    } catch (e: any) {
      setError(e.message || 'Failed to get an answer');
    } finally {
      setAsking(false);
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* ── Previous chats sidebar (collapsible so it doesn't take up space when not needed) ── */}
      <Box sx={{
        width: sidebarExpanded ? 260 : 56, flexShrink: 0,
        borderRight: `1px solid ${palette.tokens.hairline}`,
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
        overflow: 'hidden', transition: 'width 0.2s ease', p: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: sidebarExpanded ? 2 : 0 }}>
          {sidebarExpanded && (
            <Button
              fullWidth variant="outlined" startIcon={<NewChatIcon />} onClick={handleNewChat}
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
            >
              Start new chat
            </Button>
          )}
          <Tooltip title={sidebarExpanded ? 'Collapse' : 'Expand previous chats'}>
            <IconButton
              size="small"
              onClick={() => setSidebarExpanded(v => !v)}
              sx={{ color: palette.tokens.mutedSoft, flexShrink: 0 }}
            >
              {sidebarExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {sidebarExpanded && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, fontWeight: 700, textTransform: 'uppercase' }}>
                Previous chats
              </Typography>
              {sessions.length > 0 && (
                <Tooltip title="Clears this list only — your chat history is still saved">
                  <IconButton size="small" onClick={() => setSessions([])} sx={{ color: palette.tokens.mutedSoft }}>
                    <ClearAllIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {sessionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
            ) : sessions.length === 0 ? (
              <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>No previous chats yet.</Typography>
            ) : (
              <List sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {sessions.map((s) => (
                  <ListItemButton
                    key={s.session_id}
                    selected={s.session_id === activeSessionId}
                    onClick={() => openSession(s.session_id)}
                    sx={{ borderRadius: 2, mb: 0.5, alignItems: 'flex-start' }}
                  >
                    <ChatIcon sx={{ fontSize: 16, color: palette.tokens.mutedSoft, mt: 0.4, mr: 1, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: palette.tokens.ink }}>{s.title}</Typography>
                      <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
                        {s.message_count} message{s.message_count !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
            {usageSummary && (
              <Tooltip
                title={`Input: ${usageSummary.total_input_tokens.toLocaleString()} · Output: ${usageSummary.total_output_tokens.toLocaleString()} · Thinking: ${usageSummary.total_thinking_tokens.toLocaleString()} tokens across ${usageSummary.total_questions} question${usageSummary.total_questions !== 1 ? 's' : ''}. Estimated cost, not billing-grade — see backend pricing notes.`}
              >
                <Box sx={{
                  mt: 2, pt: 1.5, borderTop: `1px solid ${palette.tokens.hairline}`,
                  display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default',
                }}>
                  <TokenIcon sx={{ fontSize: 14, color: palette.tokens.mutedSoft }} />
                  <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
                    {usageSummary.total_tokens.toLocaleString()} tokens · {fmtCostUsd(usageSummary.total_cost_usd)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {credits && (
              <Tooltip title="$1/month of AI usage per user, shown as 1000 credits (1 credit = $0.001) — resets at the start of each calendar month.">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, cursor: 'default' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: credits.credits_remaining <= 0 ? 'error.main' : palette.tokens.mutedSoft, fontWeight: credits.credits_remaining <= 0 ? 700 : 400 }}
                  >
                    {Math.max(credits.credits_remaining, 0).toFixed(1)} / {credits.credits_monthly_allowance.toFixed(0)} credits left
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* ── Active chat ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 820, mx: 'auto' }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <AgentIcon sx={{ color: palette.tokens.primary }} />
            <Typography variant="displaySm" sx={{ fontSize: '1.75rem', color: palette.tokens.ink, lineHeight: 1.2 }}>
              Ask AI
            </Typography>
          </Box>
          <Typography sx={{ color: palette.tokens.muted, fontSize: 14, mb: 3 }}>
            Ask a question about your own portfolio — compliance status, rent collected, expenses.
          </Typography>

          <Paper elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 3, p: 2, mb: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {!error && credits && credits.credits_remaining <= 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You've used all your AI credits for this month. Your balance resets at the start of next month.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="e.g. How many certificates are expiring soon? How much rent did I collect this month?"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAsk();
                  }
                }}
                disabled={asking || (!!credits && credits.credits_remaining <= 0)}
              />
              <Button
                variant="contained"
                onClick={handleAsk}
                disabled={asking || !question.trim() || (!!credits && credits.credits_remaining <= 0)}
                sx={{ minWidth: 48, px: 2 }}
              >
                {asking ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </Button>
            </Box>
          </Paper>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : history.length === 0 ? (
            <Typography sx={{ color: palette.tokens.mutedSoft, textAlign: 'center', py: 4 }}>
              Ask your first question above.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip title="Clears this view only — your question history is still saved">
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => setHistory([])}
                    sx={{ textTransform: 'none', color: palette.tokens.mutedSoft }}
                  >
                    Clear
                  </Button>
                </Tooltip>
              </Box>
              {history.slice().reverse().map((h) => {
                const isOpen = openIds.has(h.id);
                return (
                  <Paper key={h.id} elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 3, overflow: 'hidden' }}>
                    <Box
                      onClick={() => toggleOpen(h.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 2.5,
                        pb: isOpen ? 1 : 2.5, cursor: 'pointer',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: palette.tokens.ink, flex: 1 }}>{h.question}</Typography>
                      <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft, flexShrink: 0 }}>
                        {new Date(h.created_at).toLocaleString()}
                      </Typography>
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          color: palette.tokens.mutedSoft, flexShrink: 0,
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s ease',
                        }}
                      />
                    </Box>
                    {isOpen && (
                      <Box sx={{ px: 2.5, pb: 2.5 }}>
                        {h.error ? (
                          <Alert severity="error" sx={{ mt: 1 }}>{h.error}</Alert>
                        ) : (
                          <>
                            <AnswerText text={h.answer || ''} items={h.items || []} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                              {h.confidence !== null && (
                                <Chip
                                  size="small"
                                  label={`Confidence: ${Math.round((h.confidence || 0) * 100)}%`}
                                  sx={{ fontSize: 11, fontWeight: 600 }}
                                />
                              )}
                              {h.data_sources_used.map(src => (
                                <Chip key={src} size="small" variant="outlined" label={src} sx={{ fontSize: 11 }} />
                              ))}
                            </Box>
                            {h.caveats && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: palette.tokens.mutedSoft, fontStyle: 'italic' }}>
                                {h.caveats}
                              </Typography>
                            )}
                          </>
                        )}
                        {!h.error && h.total_tokens > 0 && (
                          <Tooltip title={`Model: ${h.model || 'unknown'} · Input: ${h.input_tokens.toLocaleString()} · Output: ${h.output_tokens.toLocaleString()} · Thinking: ${h.thinking_tokens.toLocaleString()} tokens. Estimated cost, not billing-grade.`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, cursor: 'default' }}>
                              <TokenIcon sx={{ fontSize: 12, color: palette.tokens.mutedSoft }} />
                              <Typography variant="caption" sx={{ color: palette.tokens.mutedSoft }}>
                                {h.model ? `${h.model} · ` : ''}{h.total_tokens.toLocaleString()} tokens ({h.input_tokens.toLocaleString()} in / {h.output_tokens.toLocaleString()} out
                                {h.thinking_tokens > 0 ? ` / ${h.thinking_tokens.toLocaleString()} thinking` : ''}) · {fmtCostUsd(h.estimated_cost_usd)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AskAIPage;
